import type { FinanceInfo } from '@carvana/merch-contracts';
import FinancingType from '@carvana/merch-contracts/FinancingType';
import {
  defaultLoadableState,
  LoadableState,
  useLoadableState
} from '@carvana/merch-react-hooks';
import deepmerge from 'deepmerge';
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';
import { calculateDaysUntilExpired } from '@/common/FinanceCard/utils';
import type { FinancingAdapterUserCreditStatusSummary } from '~/contracts/common';
import type { DeepPartial } from '~/types';
import inventoryCache, { getSrpCacheKey } from '~/utilities/srp/inventoryCache';
import getFinancingType from './getFinancingType';

export type ContextType = {
  creditStatus: LoadableState<Partial<FinancingAdapterUserCreditStatusSummary>>;
  financeInfo: FinanceInfo;
  isFinanceInitialized: boolean;
  setFinanceInfo: (financeInfo: FinanceInfo | undefined) => void;
  setIsFinanceInitialized: React.Dispatch<React.SetStateAction<boolean>>;
  setShouldRefreshSoftPull: React.Dispatch<React.SetStateAction<boolean>>;
  shouldRefreshSoftPull: boolean;
};

export const defaultContext: ContextType = {
  creditStatus: defaultLoadableState<
    Partial<FinancingAdapterUserCreditStatusSummary>
  >({}),
  financeInfo: {
    coApplicantToggleOn: undefined,
    coApplicantEligibility: undefined,
    creditApplicationExpirationUtc: undefined,
    financingType: FinancingType.EstimatedFinancing,
    hasCoApplicant: undefined,
    maxMonthlyPayment: 2000,
    maxTerm: undefined,
    minDownPayment: 0,
    minDownPaymentNoTrade: undefined
  },
  isFinanceInitialized: false,
  setFinanceInfo() {},
  setIsFinanceInitialized() {},
  setShouldRefreshSoftPull() {},
  shouldRefreshSoftPull: false
};

const FinanceContext = createContext<ContextType>(defaultContext);

export type OwnProps = React.PropsWithChildren<{
  financeInfo?: FinanceInfo;
}>;

export function FinanceContextProvider(props: OwnProps) {
  const creditStatus = useLoadableState<
    Partial<FinancingAdapterUserCreditStatusSummary>
  >({});

  // A client-cache inventory restore skips useGetInventory's fetch, the only
  // place that sets isFinanceInitialized for a logged-in user — seed from the
  // cached snapshot instead so the Finance card doesn't stay stuck loading.
  const [financeCacheSeed] = useState(() => {
    const key = getSrpCacheKey();

    return key ? inventoryCache.getFinanceSnapshot(key) : null;
  });

  const [financeInfo, setFinanceInfo] = useState(
    financeCacheSeed?.financeInfo ??
      props.financeInfo ??
      defaultContext.financeInfo
  );
  const [isFinanceInitialized, setIsFinanceInitialized] = useState(
    Boolean(financeCacheSeed) || Boolean(props.financeInfo)
  );
  const [shouldRefreshSoftPull, setShouldRefreshSoftPull] = useState(false);

  const setFinanceInfoWithDefault = useCallback(
    (nextFinanceInfo: FinanceInfo | undefined) => {
      setFinanceInfo(
        prevFinanceInfo =>
          nextFinanceInfo ?? {
            ...defaultContext.financeInfo,
            ...prevFinanceInfo
          }
      );
    },
    []
  );

  const value = useMemo<ContextType>(
    () => ({
      creditStatus,
      financeInfo,
      isFinanceInitialized,
      setIsFinanceInitialized,
      setFinanceInfo: setFinanceInfoWithDefault,
      setShouldRefreshSoftPull,
      shouldRefreshSoftPull
    }),
    [
      creditStatus,
      financeInfo,
      isFinanceInitialized,
      setIsFinanceInitialized,
      setFinanceInfoWithDefault,
      setShouldRefreshSoftPull,
      shouldRefreshSoftPull
    ]
  );

  return (
    <FinanceContext.Provider value={value}>
      {props.children}
    </FinanceContext.Provider>
  );
}

FinanceContextProvider.displayName = 'FinanceContextProvider';

export const generateFinanceContext = (
  customState: DeepPartial<ContextType>
): ContextType =>
  deepmerge<ContextType, DeepPartial<ContextType>>(defaultContext, customState);

export function MockedFinanceContextProvider({
  children,
  mockValue
}: React.PropsWithChildren<{
  mockValue: DeepPartial<ContextType>;
}>) {
  return (
    <FinanceContext.Provider value={generateFinanceContext(mockValue)}>
      {children}
    </FinanceContext.Provider>
  );
}

export function useCreditStatus(): LoadableState<
  Partial<FinancingAdapterUserCreditStatusSummary>
> {
  return useContext(FinanceContext).creditStatus;
}

export function useDaysToExpired(): number {
  return calculateDaysUntilExpired(
    useContext(FinanceContext).financeInfo.creditApplicationExpirationUtc ??
      null
  );
}

export function useFinanceInfo(): FinanceInfo {
  return useContext(FinanceContext).financeInfo ?? defaultContext.financeInfo;
}

export function useFinanceType(): FinancingType | undefined {
  const {
    creditStatus: {
      data: { creditStatus }
    },
    isFinanceInitialized,
    financeInfo: { financingType }
  } = useContext(FinanceContext);

  // Use Search API credit status when returned otherwise use Credit Status API
  return isFinanceInitialized
    ? financingType
    : getFinancingType({
        hasExpiredSoftPull: creditStatus?.hasExpiredSoftPull ?? false,
        hasValidSoftPull: creditStatus?.hasValidSoftPull ?? false
      });
}

export function useHasSoftPull(): boolean {
  return useFinanceType() === FinancingType.UserFinancing;
}

export function useHasEstimatedTerms(): boolean {
  return useFinanceType() === FinancingType.EstimatedFinancing;
}

export function useHasExpiredSoftPull(): boolean {
  return useFinanceType() === FinancingType.Expired;
}

export function useShouldRefreshSoftPull(): boolean {
  return useContext(FinanceContext).shouldRefreshSoftPull;
}

export function useIsFinanceInitialized(): boolean {
  return useContext(FinanceContext).isFinanceInitialized;
}

export default FinanceContext;
